<template>
	<div class="container">
		<div class="content">
			<div class="breadcrumb">
				<el-breadcrumb separator-class="el-icon-arrow-right">
					<el-breadcrumb-item replace :to="{ path: '/my/index' }">个人中心</el-breadcrumb-item>
					<el-breadcrumb-item replace :to="{ path: '/my/claimRecord' }">我的理赔</el-breadcrumb-item>
					<el-breadcrumb-item>理赔详情</el-breadcrumb-item>
				</el-breadcrumb>
			</div>

			<!-- 状态信息 -->
			<div class="box">
				<div class="box-title">
					{{ getStatusText(data.case_status) }}
				</div>
				
				<!-- 收款账户信息 -->
				<div class="basic-info" v-if="data.account_name">
					<div class="basic-item">
						<div class="basic-item-title">
							持卡人姓名
						</div>
						<div class="basic-item-value">
							{{ data.account_name }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							银行卡号
						</div>
						<div class="basic-item-value">
							{{ data.account_code }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							开户行
						</div>
						<div class="basic-item-value">
							{{ data.bank_name_cn || data.bank_name }}
						</div>
					</div>
					<div class="basic-item" v-if="data.claim_amount > 0">
						<div class="basic-item-title">
							赔付金额
						</div>
						<div class="basic-item-value" style="color: #2280FD; font-weight: bold;">
							¥{{ data.claim_amount }}
						</div>
					</div>
					<div class="basic-item" v-if="data.claim_time">
						<div class="basic-item-title">
							赔付时间
						</div>
						<div class="basic-item-value">
							{{ formatDate(data.claim_time) }}
						</div>
					</div>
				</div>
			</div>
			
			<!-- 进度详情 -->
			<div class="box">
				<div class="box-title">
					进度详情
				</div>
				
				<el-timeline>
					<el-timeline-item 
						v-for="(step, index) in progressSteps" 
						:key="index"
						:timestamp="step.time" 
						:color="step.active ? '#2280FD' : '#C0C4CC'"
						:placement="index % 2 === 0 ? 'top' : 'bottom'"
					>
						{{ step.name }}
						<div v-if="step.reason" class="reject-reason">
							驳回原因：{{ step.reason }}
						</div>
					</el-timeline-item>
				</el-timeline>
			</div>
			
			<!-- 报案信息 -->
			<div class="box">
				<div class="box-title">
					报案信息
				</div>
				
				<div class="basic-info">
					<div class="basic-item">
						<div class="basic-item-title">
							报案号
						</div>
						<div class="basic-item-value">
							{{ data.register_no || '--' }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							保险产品
						</div>
						<div class="basic-item-value">
							{{ data.goods_name }} {{ data.goods_specs_name }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							承保公司
						</div>
						<div class="basic-item-value">
							{{ data.insurer_name }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							报案时间
						</div>
						<div class="basic-item-value">
							{{ formatDate(data.regist_time) }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							报案地点
						</div>
						<div class="basic-item-value">
							{{ data.regist_address || '--' }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							报案人姓名
						</div>
						<div class="basic-item-value">
							{{ data.reporter || '--' }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							报案人电话
						</div>
						<div class="basic-item-value">
							{{ data.reporter_telephone || '--' }}
						</div>
					</div>
					<div class="basic-item" style="width: 100%;">
						<div class="basic-item-title">
							赔案号
						</div>
						<div class="basic-item-value">
							{{ data.claim_no || '--' }}
						</div>
					</div>
					<div class="basic-item" style="width: 100%;">
						<div class="basic-item-title">
							赔款序号
						</div>
						<div class="basic-item-value">
							{{ data.sequence || '--' }}
						</div>
					</div>
				</div>
			</div>
			
			<!-- 被保险人信息 -->
			<div class="box">
				<div class="box-title">
					被保险人信息
				</div>
				
				<div class="basic-info">
					<div class="basic-item">
						<div class="basic-item-title">
							保单号
						</div>
						<div class="basic-item-value">
							{{ data.policy_no }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							被保险人姓名
						</div>
						<div class="basic-item-value">
							{{ data.insurant_name }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							证件类型
						</div>
						<div class="basic-item-value">
							{{ getCertTypeText(data.insurant_cert_type) }}
						</div>
					</div>
					<div class="basic-item">
						<div class="basic-item-title">
							证件号码
						</div>
						<div class="basic-item-value">
							{{ data.insurant_cert_no }}
						</div>
					</div>
				</div>
			</div>
			
			<!-- 出险信息 -->
			<div class="box">
				<div class="box-title">
					出险信息
				</div>
				
				<div class="basic-info">
					<div class="basic-item" style="width: 100%;">
						<div class="basic-item-title">
							出险经过
						</div>
						<div class="basic-item-value" style="width: 85%; line-height: 1.6;">
							{{ data.accident_desc || '--' }}
						</div>
					</div>
				</div>
			</div>
			
			<!-- 材料图片 -->
			<div class="box">
				<div class="box-title">
					材料图片
				</div>
				
				<div class="file-list">
					<div class="file-title">
						门诊病历
					</div>
					<page-images v-model="data.outpatient_record_file" :limit="5" disabled/>
				</div>
				
				<div class="file-list">
					<div class="file-title">
						医疗费用发票
					</div>
					<page-images v-model="data.medical_invoice_file" :limit="5" disabled/>
				</div>
				
				<div class="file-list">
					<div class="file-title">
						费用明细清单
					</div>
					<page-images v-model="data.cost_detail_file" :limit="5" disabled/>
				</div>
				
				<div class="file-list">
					<div class="file-title">
						检查报告
					</div>
					<page-images v-model="data.check_report_file" :limit="5" disabled/>
				</div>
			</div>
			
			<!-- 退票信息 -->
			<div class="box" v-if="data.return_reason">
				<div class="box-title">
					退票信息
				</div>
				
				<div class="basic-info">
					<div class="basic-item" style="width: 100%;">
						<div class="basic-item-title">
							退票原因
						</div>
						<div class="basic-item-value" style="color: #F56C6C;">
							{{ data.return_reason }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<mainFooter />
	</div>
</template>

<script>
	import mainFooter from '../../../../src/basic/components/layout/mainFooter.vue';
	
	// 理赔状态映射
	const CASE_STATUS_MAP = {
		'01': '立案录入中',
		'02': '立案审核中',
		'03': '定损中',
		'04': '核损中',
		'05': '核损初审中',
		'06': '核损复审中',
		'07': '单证确认中',
		'08': '结案理算中',
		'09': '结案核赔中',
		'10': '已决未结（正常结案）',
		'11': '已决已结（正常结案）',
		'12': '已决已结（零赔付）',
		'13': '已决已结（拒赔）',
		'14': '已决已结（立案注销）',
		'15': '报案注销',
		'16': '已决未结（零结案）',
		'17': '已决未结（拒赔）',
		'18': '已决未结（立案注销）',
		'19': '结案理算（零注拒）',
		'20': '结案核赔（零注拒）',
		'25': '已决未结',
		'26': '已决已结',
		'27': '结案核赔',
		'28': '结案理算',
		'29': '核价中'
	};
	
	// 证件类型映射
	const CERT_TYPE_MAP = {
		'01': '身份证',
		'02': '护照',
		'03': '军官证',
		'04': '驾驶证',
		'05': '港澳通行证',
		'06': '台湾通行证',
		'07': '其他'
	};

	export default {
		components: {
			mainFooter,
		},
		data() {
			return {
				data: {},
			}
		},
		computed: {
			// 进度步骤
			progressSteps() {
				const steps = [
					{ name: '提交申请', time: this.formatDate(this.data.created_at), active: true },
				];
				
				// 根据状态动态生成进度
				if (this.data.case_status && this.data.case_status !== '01') {
					steps.push({ 
						name: '材料审核', 
						time: this.formatDate(this.data.updated_at), 
						active: true,
						reason: this.data.return_reason 
					});
				}
				
				if (['03','04','05','06','07','08','09','10','11','12','13','14','25','26','27','28','29'].includes(this.data.case_status)) {
					steps.push({ 
						name: '理赔审核', 
						time: '--', 
						active: true 
					});
				}
				
				if (['11','12','13','14','26'].includes(this.data.case_status)) {
					steps.push({ 
						name: '理赔完结', 
						time: this.formatDate(this.data.claim_time), 
						active: true 
					});
				}
				
				return steps;
			}
		},
		mounted() {
			this.getData(this.$route.query.id);
		},
		methods: {
			getData(id) {
				if (!id) return;
				this.$load();
				this.$get('app-order-claim/' + id).then(res => {
					this.data = res.data || {};
				}).catch(err => {
					this.$message.error(err);
				})
			},
			
			// 获取状态文本
			getStatusText(status) {
				return CASE_STATUS_MAP[status] || '材料审核中';
			},
			
			// 获取证件类型文本
			getCertTypeText(type) {
				return CERT_TYPE_MAP[type] || type || '身份证';
			},
			
			// 格式化日期
			formatDate(date) {
				if (!date) return '--';
				try {
					return new Date(date).toLocaleString('zh-CN', {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
						hour: '2-digit',
						minute: '2-digit'
					}).replace(/\//g, '-');
				} catch {
					return '--';
				}
			},
		},
	}
</script>

<style lang="scss" scoped>
	.container {
		width: 100%;
		background-color: #F7F7F7;

		.content {
			min-height: calc(100vh - 300px);
			margin: 20px auto 40px;
			width: 68%;
			
			// 笔记本适配
			@media (max-width: 1440px) {
				width: 80%;
			}
			
			@media (max-width: 1280px) {
				width: 90%;
			}
			
			@media (max-width: 1024px) {
				width: 95%;
				margin: 15px auto 30px;
			}
			
			.box {
				margin-top: 20px;
				padding: 20px;
				border-radius: 10px;
				background-color: #fff;
				
				// 笔记本适配
				@media (max-width: 1024px) {
					padding: 15px;
					margin-top: 15px;
				}
				
				.box-title {
					color: #333333;
					font-size: 20px;
					font-weight: bold;
					margin-bottom: 20px;
					padding-bottom: 10px;
					border-bottom: 1px solid #f0f0f0;
					
					// 笔记本适配
					@media (max-width: 1280px) {
						font-size: 18px;
						margin-bottom: 15px;
					}
				}
				
				.basic-info {
					width: 100%;
					display: flex;
					flex-wrap: wrap;
					padding-bottom: 20px;
					margin-top: -15px;
					
					// 笔记本适配
					@media (max-width: 1280px) {
						padding-bottom: 15px;
					}
					
					.basic-item {
						display: flex;
						width: 33.3%;
						margin-top: 15px;
						font-size: 14px;
						
						// 笔记本适配 - 在较小屏幕上改为两列
						@media (max-width: 1024px) {
							width: 50%;
						}
						
						// 在更小屏幕上改为单列
						@media (max-width: 768px) {
							width: 100%;
						}
						
						.basic-item-title {
							width: 100px;
							color: #666666;
							flex-shrink: 0;
							
							// 笔记本适配
							@media (max-width: 1280px) {
								width: 90px;
								font-size: 13px;
							}
							
							@media (max-width: 768px) {
								width: 100px;
							}
						}
						
						.basic-item-value {
							color: #333333;
							flex: 1;
							word-break: break-all;
							
							// 笔记本适配
							@media (max-width: 1280px) {
								font-size: 13px;
							}
						}
						
						// 全宽项目的适配
						&[style*="width: 100%"] {
							.basic-item-value {
								// 笔记本适配
								@media (max-width: 1280px) {
									width: 80%;
								}
								
								@media (max-width: 768px) {
									width: 70%;
								}
							}
						}
					}
				}
				
				.file-list {
					margin-bottom: 20px;
					
					// 笔记本适配
					@media (max-width: 1280px) {
						margin-bottom: 15px;
					}
					
					.file-title {
						color: #666666;
						font-size: 14px;
						margin-bottom: 10px;
						
						// 笔记本适配
						@media (max-width: 1280px) {
							font-size: 13px;
						}
					}
				}
				
				.reject-reason {
					color: #F56C6C;
					font-size: 12px;
					margin-top: 5px;
					background: #FEF0F0;
					padding: 5px 8px;
					border-radius: 4px;
					border-left: 3px solid #F56C6C;
					
					// 笔记本适配
					@media (max-width: 768px) {
						font-size: 11px;
						padding: 4px 6px;
					}
				}
				
				// 时间轴适配
				:deep(.el-timeline) {
					.el-timeline-item {
						// 笔记本适配
						@media (max-width: 1280px) {
							padding-bottom: 15px;
						}
						
						.el-timeline-item__timestamp {
							// 笔记本适配
							@media (max-width: 1280px) {
								font-size: 12px;
							}
						}
						
						.el-timeline-item__content {
							// 笔记本适配
							@media (max-width: 1280px) {
								font-size: 13px;
							}
						}
					}
				}
			}
		}
	}
</style>